/**
 * Client Portal SSE Hook - Real-time Messaging
 * Sprint 3.3 - US-3.3.4: Messagerie Temps Réel
 *
 * Gère la connexion SSE et les événements temps réel
 * Pattern: Hook → EventSource → Callbacks
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getPortalToken } from '../services/client-portal.service';
import { clientPortalKeys } from './useClientPortal';
import type {
  SSEConnectionState,
  PortalSSEEventType,
  NewMessageSSEEvent,
  UnreadUpdateSSEEvent,
  TypingSSEEvent,
  ConnectedSSEEvent,
  ClientMessage,
} from '../types/client-portal.types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
const SSE_URL = `${API_BASE}/portal/sse/events`;

// Reconnection parameters
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const RECONNECT_MULTIPLIER = 1.5;

interface UseClientPortalSSEOptions {
  /** Callback when a new message is received */
  onNewMessage?: (event: NewMessageSSEEvent) => void;
  /** Callback when unread count updates */
  onUnreadUpdate?: (event: UnreadUpdateSSEEvent) => void;
  /** Callback when someone starts typing */
  onTypingStart?: (event: TypingSSEEvent) => void;
  /** Callback when someone stops typing */
  onTypingStop?: (event: TypingSSEEvent) => void;
  /** Callback on connection state change */
  onConnectionChange?: (state: SSEConnectionState) => void;
  /** Disable auto-connection */
  disabled?: boolean;
}

interface UseClientPortalSSEReturn {
  /** Current connection state */
  connectionState: SSEConnectionState;
  /** Whether connected */
  isConnected: boolean;
  /** Manually connect */
  connect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
  /** List of users currently typing (by projectId) */
  typingUsers: Map<string, string[]>;
}

/**
 * Hook for Client Portal real-time SSE events
 *
 * Automatically connects when mounted and handles reconnection.
 * Updates TanStack Query cache when new messages arrive.
 */
export function useClientPortalSSE(
  options: UseClientPortalSSEOptions = {}
): UseClientPortalSSEReturn {
  const {
    onNewMessage,
    onUnreadUpdate,
    onTypingStart,
    onTypingStop,
    onConnectionChange,
    disabled = false,
  } = options;

  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);

  const [connectionState, setConnectionState] = useState<SSEConnectionState>('disconnected');
  const [typingUsers, setTypingUsers] = useState<Map<string, string[]>>(new Map());

  // Connection state updater
  const updateConnectionState = useCallback(
    (state: SSEConnectionState) => {
      setConnectionState(state);
      onConnectionChange?.(state);
    },
    [onConnectionChange]
  );

  // Handle new message event
  const handleNewMessage = useCallback(
    (event: NewMessageSSEEvent) => {
      // Update TanStack Query cache for project messages
      queryClient.setQueryData<ClientMessage[]>(
        clientPortalKeys.messagesByProject(event.projectId),
        (old) => {
          if (!old) return [event.message];
          // Avoid duplicates
          if (old.some((m) => m.id === event.message.id)) return old;
          return [...old, event.message];
        }
      );

      // Invalidate conversations list to refresh
      queryClient.invalidateQueries({
        queryKey: clientPortalKeys.conversations(),
      });

      // Call user callback
      onNewMessage?.(event);
    },
    [queryClient, onNewMessage]
  );

  // Handle unread update event
  const handleUnreadUpdate = useCallback(
    (event: UnreadUpdateSSEEvent) => {
      // Update unread count in cache
      queryClient.setQueryData(clientPortalKeys.unreadCount(), { count: event.totalUnread });

      // Invalidate conversations to refresh unread counts
      queryClient.invalidateQueries({
        queryKey: clientPortalKeys.conversations(),
      });

      // Call user callback
      onUnreadUpdate?.(event);
    },
    [queryClient, onUnreadUpdate]
  );

  // Handle typing events
  const handleTypingStart = useCallback(
    (event: TypingSSEEvent) => {
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        const current = newMap.get(event.projectId) || [];
        if (!current.includes(event.userName)) {
          newMap.set(event.projectId, [...current, event.userName]);
        }
        return newMap;
      });
      onTypingStart?.(event);
    },
    [onTypingStart]
  );

  const handleTypingStop = useCallback(
    (event: TypingSSEEvent) => {
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        const current = newMap.get(event.projectId) || [];
        newMap.set(
          event.projectId,
          current.filter((u) => u !== event.userName)
        );
        return newMap;
      });
      onTypingStop?.(event);
    },
    [onTypingStop]
  );

  // Connect to SSE
  const connect = useCallback(() => {
    const token = getPortalToken();
    if (!token) {
      updateConnectionState('error');
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    updateConnectionState('connecting');

    // Create EventSource with auth token in URL (SSE doesn't support headers)
    const url = `${SSE_URL}?token=${encodeURIComponent(token)}`;
    const eventSource = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = eventSource;

    // Connection opened
    eventSource.onopen = () => {
      updateConnectionState('connected');
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
    };

    // Handle SSE events
    eventSource.addEventListener('connected', (e) => {
      const data: ConnectedSSEEvent = JSON.parse(e.data);
      // SSE connected event received
    });

    eventSource.addEventListener('message:new', (e) => {
      const data: NewMessageSSEEvent = JSON.parse(e.data);
      handleNewMessage(data);
    });

    eventSource.addEventListener('message:read', (e) => {
      const data = JSON.parse(e.data);
      // Invalidate messages to refresh read status
      queryClient.invalidateQueries({
        queryKey: clientPortalKeys.messagesByProject(data.projectId),
      });
    });

    eventSource.addEventListener('unread:update', (e) => {
      const data: UnreadUpdateSSEEvent = JSON.parse(e.data);
      handleUnreadUpdate(data);
    });

    eventSource.addEventListener('typing:start', (e) => {
      const data: TypingSSEEvent = JSON.parse(e.data);
      handleTypingStart(data);
    });

    eventSource.addEventListener('typing:stop', (e) => {
      const data: TypingSSEEvent = JSON.parse(e.data);
      handleTypingStop(data);
    });

    eventSource.addEventListener('heartbeat', () => {
      // Heartbeat received, connection is alive
    });

    eventSource.addEventListener('shutdown', () => {
      // SSE server shutting down
      disconnect();
    });

    // Handle errors and reconnection
    eventSource.onerror = () => {
      updateConnectionState('error');
      eventSource.close();
      eventSourceRef.current = null;

      // Schedule reconnection with exponential backoff
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        if (!disabled) {
          reconnectDelayRef.current = Math.min(
            reconnectDelayRef.current * RECONNECT_MULTIPLIER,
            MAX_RECONNECT_DELAY
          );
          connect();
        }
      }, reconnectDelayRef.current);
    };
  }, [
    disabled,
    updateConnectionState,
    handleNewMessage,
    handleUnreadUpdate,
    handleTypingStart,
    handleTypingStop,
    queryClient,
  ]);

  // Disconnect from SSE
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    updateConnectionState('disconnected');
  }, [updateConnectionState]);

  // Auto-connect on mount, cleanup on unmount
  useEffect(() => {
    if (!disabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [disabled, connect, disconnect]);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    connect,
    disconnect,
    typingUsers,
  };
}

export default useClientPortalSSE;
